/**
 * ⌘K command palette — search-as-you-type over the vault + fixed "Go to…"
 * navigation actions, full keyboard nav (↑/↓ to move, ↵ to activate, esc to
 * close). Shares the visual language of the palette design spec.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { SearchHit } from '@vault/shared';
import { Favicon, Icon } from '@vault/ui';
import type { IconName } from '@vault/ui';
import { api } from '../api/client.js';
import {
  browserLabel,
  faviconIndex,
  faviconLetter,
  fmtDate,
  hostname,
  matchedReason,
} from '../pages/search/searchHelpers.js';

interface NavAction {
  id: string;
  label: string;
  icon: IconName;
  to: string;
}

const NAV_ACTIONS: NavAction[] = [
  { id: 'timeline', label: 'Timeline', icon: 'clock', to: '/timeline' },
  { id: 'duplicates', label: 'Duplicates', icon: 'grid', to: '/duplicates' },
  { id: 'collections', label: 'Collections', icon: 'folder', to: '/collections' },
  { id: 'import', label: 'Import', icon: 'download', to: '/import' },
  { id: 'settings', label: 'Settings', icon: 'gear', to: '/settings' },
];

type PaletteItem = { kind: 'action'; action: NavAction } | { kind: 'hit'; hit: SearchHit };

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  const [q, setQ] = useState('');
  const [dq, setDq] = useState('');
  const [active, setActive] = useState(0);

  // fresh, focused field each time the palette opens
  useEffect(() => {
    if (!open) return;
    setQ('');
    setDq('');
    setActive(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  // debounce the query used for the live search
  useEffect(() => {
    const id = window.setTimeout(() => setDq(q.trim()), 150);
    return () => window.clearTimeout(id);
  }, [q]);

  // reset the cursor to the top whenever the query changes
  useEffect(() => setActive(0), [q]);

  const usage = useQuery({ queryKey: ['usage'], queryFn: () => api.account.usage(), enabled: open });

  const search = useQuery({
    queryKey: ['palette-search', dq],
    queryFn: () => api.search(dq, { mode: 'hybrid', limit: 6 }),
    enabled: open && dq.length > 0,
    placeholderData: keepPreviousData,
  });

  const nq = q.trim().toLowerCase();
  const actions = useMemo(
    () => (nq ? NAV_ACTIONS.filter((a) => a.label.toLowerCase().includes(nq)) : NAV_ACTIONS),
    [nq],
  );
  const hits = q.trim().length > 0 ? search.data?.hits ?? [] : [];

  const items = useMemo<PaletteItem[]>(
    () => [
      ...actions.map((a): PaletteItem => ({ kind: 'action', action: a })),
      ...hits.map((h): PaletteItem => ({ kind: 'hit', hit: h })),
    ],
    [actions, hits],
  );

  // keep the active index within bounds as results stream in / filter out
  useEffect(() => {
    setActive((i) => (items.length === 0 ? 0 : Math.min(i, items.length - 1)));
  }, [items.length]);

  if (!open) return null;

  const activate = (item: PaletteItem | undefined) => {
    if (!item) return;
    if (item.kind === 'action') {
      navigate(item.action.to);
    } else {
      window.open(item.hit.url, '_blank', 'noopener,noreferrer');
    }
    onClose();
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (items.length === 0 ? 0 : (i + 1) % items.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (items.length === 0 ? 0 : (i - 1 + items.length) % items.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      activate(items[active]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  const showResultsSection = q.trim().length > 0;
  const loadingHits = showResultsSection && search.isLoading;

  return (
    <div className="tv-cmd-overlay" onClick={onClose}>
      <style>{CSS}</style>
      <div
        className="tv-cmd"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
      >
        {/* input */}
        <div className="tv-cmd__head">
          <Icon name="search" size={16} color="var(--text-3)" />
          <input
            ref={inputRef}
            className="tv-cmd__input"
            placeholder="Search your vault or jump to…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <span className="tv-cmd__kbd">ESC</span>
        </div>

        {/* scrollable body */}
        <div className="tv-cmd__body">
          {actions.length > 0 && (
            <div className="tv-cmd__section">
              <span className="tv-cmd__label">GO TO…</span>
              {actions.map((a, i) => {
                const idx = i;
                const isActive = active === idx;
                return (
                  <div
                    key={a.id}
                    className="tv-cmd-row"
                    data-active={isActive}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => activate({ kind: 'action', action: a })}
                  >
                    <span
                      className="tv-cmd-ico"
                      style={{ background: isActive ? 'var(--primary)' : 'var(--surface-3)' }}
                    >
                      <Icon name={a.icon} size={13} color={isActive ? '#fff' : 'var(--text-2)'} />
                    </span>
                    <span className="tv-cmd-title">{a.label}</span>
                    <span className="tv-cmd-hint">{isActive ? 'GO TO ↵' : ''}</span>
                  </div>
                );
              })}
            </div>
          )}

          {showResultsSection && (
            <div className="tv-cmd__section">
              <span className="tv-cmd__label">BEST MATCHES · “{q.trim()}”</span>

              {loadingHits && (
                <>
                  {[0, 1].map((i) => (
                    <div key={i} className="tv-cmd-row" style={{ cursor: 'default' }}>
                      <span className="tv-skeleton" style={{ width: 20, height: 20, borderRadius: 6 }} />
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
                        <span className="tv-skeleton" style={{ width: '50%', height: 11, borderRadius: 4 }} />
                        <span className="tv-skeleton" style={{ width: '75%', height: 9, borderRadius: 4 }} />
                      </span>
                    </div>
                  ))}
                </>
              )}

              {!loadingHits && hits.length === 0 && (
                <span className="tv-cmd__empty">No matches — try different words, or jump somewhere above.</span>
              )}

              {!loadingHits &&
                hits.map((h, j) => {
                  const idx = actions.length + j;
                  const isActive = active === idx;
                  const domain = hostname(h.url);
                  const b = browserLabel(h.browser);
                  const reason = matchedReason(h.matchedOn);
                  return (
                    <div
                      key={h.itemId}
                      className="tv-cmd-row"
                      data-active={isActive}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => activate({ kind: 'hit', hit: h })}
                    >
                      <Favicon letter={faviconLetter(domain)} index={faviconIndex(domain)} size={20} ring="var(--surface-2)" />
                      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0, flex: 1 }}>
                        <span className="tv-cmd-title" style={{ fontWeight: 500 }}>
                          {h.title}
                        </span>
                        <span className="tv-cmd-meta">
                          PAGE · {domain} · {fmtDate(h.savedAt)}
                          {reason ? ` · ${reason.label.toUpperCase()}` : b ? ` · ${b}` : ''}
                        </span>
                      </span>
                      <span className="tv-cmd-hint" data-strong={isActive}>
                        JUMP TO ↵
                      </span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>

        {/* footer */}
        <div className="tv-cmd__foot">
          <span className="tv-cmd__foothint">↑↓ NAVIGATE</span>
          <span className="tv-cmd__foothint">↵ OPEN</span>
          <span className="tv-cmd__foothint">ESC CLOSE</span>
          <span className="tv-cmd__foothint" style={{ marginLeft: 'auto' }}>
            {usage.data
              ? `SEARCHES ${usage.data.counts.items.toLocaleString()} TABS + ACTIONS`
              : 'SEARCHES YOUR VAULT + ACTIONS'}
          </span>
        </div>
      </div>
    </div>
  );
}

const CSS = `
.tv-cmd-overlay{position:fixed;inset:0;z-index:60;background:var(--overlay);display:flex;justify-content:center;align-items:flex-start;padding:16px;padding-top:min(15vh,120px)}
.tv-cmd{width:min(640px,100%);max-height:70vh;display:flex;flex-direction:column;background:var(--surface-2);border:1px solid var(--line-strong);border-radius:var(--r-xl);box-shadow:var(--shadow-modal);overflow:hidden;font-family:var(--font-body);color:var(--text-1);animation:tv-rise .16s var(--ease)}

.tv-cmd__head{display:flex;align-items:center;gap:11px;padding:15px 18px;border-bottom:1px solid var(--line);flex-shrink:0}
.tv-cmd__input{flex:1;min-width:0;background:transparent;border:none;outline:none;color:var(--text-1);font-size:14.5px;font-family:inherit}
.tv-cmd__input::placeholder{color:var(--text-3)}
.tv-cmd__kbd{font-family:var(--font-mono);font-size:9.5px;color:var(--text-3);border:1px solid var(--line-strong);border-radius:6px;padding:2px 7px;flex-shrink:0}

.tv-cmd__body{flex:1;overflow-y:auto;padding:6px 8px}
.tv-cmd__section{padding:4px 0 8px}
.tv-cmd__label{display:block;font-family:var(--font-mono);font-size:9px;letter-spacing:.14em;color:var(--text-3);padding:8px 12px 6px}
.tv-cmd__empty{display:block;font-size:12px;color:var(--text-3);padding:6px 12px 8px}

.tv-cmd-row{display:flex;align-items:center;gap:11px;padding:10px 12px;border-radius:var(--r-md);border:1px solid transparent;cursor:pointer}
.tv-cmd-row[data-active="true"]{background:var(--accent-tint);border-color:var(--line-accent)}
.tv-cmd-ico{display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:8px;flex-shrink:0}
.tv-cmd-title{font-size:13.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0}
.tv-cmd-meta{font-family:var(--font-mono);font-size:9px;color:var(--text-3);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;letter-spacing:.03em}
.tv-cmd-hint{margin-left:auto;font-family:var(--font-mono);font-size:9px;color:var(--text-3);flex-shrink:0;white-space:nowrap}
.tv-cmd-hint[data-strong="true"]{color:var(--text-2)}

.tv-cmd__foot{display:flex;align-items:center;gap:14px;padding:10px 18px;border-top:1px solid var(--line);background:var(--surface-0);flex-shrink:0}
.tv-cmd__foothint{font-family:var(--font-mono);font-size:9px;color:var(--text-3);white-space:nowrap}
`;
